/**
 * Чистые правила ARENA (повторяют SQL-функции arena_*), чтобы их можно было проверить тестами.
 */
import {
  ARENA_DAYS,
  ARENA_MILESTONES,
  ARENA_FIRST_MEETING_DRIVES,
  ARENA_JOIN_FROM,
  ARENA_JOIN_UNTIL,
  ARENA_MAX_REWARDED_MEETINGS,
  ARENA_NEXT_MEETING_DRIVES,
  ARENA_START,
  ARENA_TZ,
} from "@/lib/arena-config";
import { MIN_INTERESTS } from "@/lib/people-constants";

/** Можно ли присоединиться в момент now. */
export function isJoinWindowOpen(now: Date): boolean {
  const t = now.getTime();
  return t >= ARENA_JOIN_FROM.getTime() && t <= ARENA_JOIN_UNTIL.getTime();
}

/** Фестиваль ещё не начался. */
export function isBeforeFestival(now: Date): boolean {
  return now.getTime() < ARENA_START.getTime();
}

/** Дата в Стамбуле: { year, month, day }. */
export function istanbulDate(now: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ARENA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

/** Какой день фестиваля сегодня по Стамбулу (24 или 25), иначе null. */
export function festivalDayToday(now: Date): 24 | 25 | null {
  const d = istanbulDate(now);
  if (d.year !== 2026 || d.month !== 10) return null;
  return (ARENA_DAYS as readonly number[]).includes(d.day) ? (d.day as 24 | 25) : null;
}

export type Presence = "today" | "tomorrow" | null;

/** Подпись на карточке: «Сегодня на ARENA» / «Будет завтра». */
export function presenceFor(days: number[] | null | undefined, now: Date): Presence {
  const list = days ?? [];
  const today = festivalDayToday(now);
  if (today && list.includes(today)) return "today";
  const d = istanbulDate(now);
  if (d.year === 2026 && d.month === 10 && list.includes(d.day + 1)) return "tomorrow";
  return null;
}

/** Пара в каноническом порядке (меньший uuid первым). Себя с собой — нельзя. */
export function canonicalPair(a: string, b: string): [string, string] {
  if (a === b) throw new Error("self_meeting");
  return a < b ? [a, b] : [b, a];
}

export type Meeting = {
  user_a: string;
  user_b: string;
  confirmed_by_a: string | null;
  confirmed_by_b: string | null;
  completed_at: string | null;
};

/**
 * Подтверждение встречи (как arena_confirm_meeting): оба должны быть участниками,
 * повторное нажатие ничего не меняет, completed_at ставится один раз.
 */
export function confirmMeeting(
  existing: Meeting | null,
  me: string,
  other: string,
  participants: Set<string>,
  nowIso: string,
): Meeting {
  if (me === other) throw new Error("self_meeting");
  if (!participants.has(me) || !participants.has(other)) throw new Error("not_participant");
  const [a, b] = canonicalPair(me, other);
  const m: Meeting = existing
    ? { ...existing }
    : { user_a: a, user_b: b, confirmed_by_a: null, confirmed_by_b: null, completed_at: null };
  if (m.user_a !== a || m.user_b !== b) throw new Error("pair_mismatch");
  if (me === a && !m.confirmed_by_a) m.confirmed_by_a = nowIso;
  if (me === b && !m.confirmed_by_b) m.confirmed_by_b = nowIso;
  if (!m.completed_at && m.confirmed_by_a && m.confirmed_by_b) m.completed_at = nowIso;
  return m;
}

/** Драйвы за n засчитанных встреч (как arena_rewarded_drives). */
export function arenaDrivesFor(completedMeetings: number): number {
  const n = Math.min(Math.max(0, completedMeetings), ARENA_MAX_REWARDED_MEETINGS);
  if (n === 0) return 0;
  return ARENA_FIRST_MEETING_DRIVES + (n - 1) * ARENA_NEXT_MEETING_DRIVES;
}

/** Выдача стикера (как arena_claim_sticker): один раз и только после встречи. */
export function claimSticker(
  state: { sticker_given_at: string | null; completedMeetings: number },
  nowIso: string,
): string {
  if (state.completedMeetings < 1) throw new Error("no_meeting");
  if (state.sticker_given_at) throw new Error("already_given");
  return nowIso;
}

/** Число подтверждённых обоими встреч — не режется лимитом наград. */
export function countOpenedOpportunities(meetings: { completed_at: string | null }[]): number {
  return meetings.filter((m) => !!m.completed_at).length;
}

export type MilestoneProgress = {
  n: number;
  /** Последняя достигнутая ступень (null при n = 0). */
  reached: number | null;
  /** Ближайшая следующая ступень (null, если достигнута последняя). */
  next: number | null;
  /** Прогресс от предыдущей ступени к следующей: done из total. */
  done: number;
  total: number;
};

export function milestoneProgress(
  n: number,
  milestones: readonly number[] = ARENA_MILESTONES,
): MilestoneProgress {
  const reached = [...milestones].reverse().find((m) => n >= m) ?? null;
  const next = milestones.find((m) => n < m) ?? null;
  return { n, reached, next, done: next === null ? 0 : n, total: next ?? 0 };
}

/** Корзины для админки: 0, 1–2, 3–4, 5–9, 10+. */
export function meetingBucket(n: number): "0" | "1–2" | "3–4" | "5–9" | "10+" {
  if (n <= 0) return "0";
  if (n <= 2) return "1–2";
  if (n <= 4) return "3–4";
  if (n <= 9) return "5–9";
  return "10+";
}

type MeetingSides = {
  user_a: string;
  user_b: string;
  confirmed_by_a: string | null;
  confirmed_by_b: string | null;
  completed_at: string | null;
};

/** Встреча ждёт МОЕГО подтверждения: не завершена, другая сторона отметила, я — нет. */
export function isPendingForMe(
  m: MeetingSides | null | undefined,
  me: string | undefined,
): boolean {
  if (!m || !me || m.completed_at) return false;
  if (m.user_a === me) return !!m.confirmed_by_b && !m.confirmed_by_a;
  if (m.user_b === me) return !!m.confirmed_by_a && !m.confirmed_by_b;
  return false;
}

/** id других людей, которые ждут моего подтверждения. */
export function pendingForMeIds(meetings: MeetingSides[], me: string | undefined): string[] {
  return meetings
    .filter((m) => isPendingForMe(m, me))
    .map((m) => (m.user_a === me ? m.user_b : m.user_a));
}

export type CoffeeInviteRow = {
  id: string;
  sender_user_id: string;
  recipient_user_id: string;
  status: string;
  responded_at: string | null;
  created_at: string;
};

export type InviteState = "none" | "sent" | "incoming" | "accepted" | "declined";

/**
 * «Принято» в coffee_invites: есть ответ (responded_at) и статус не 'declined'
 * (accepted, а также met / not_met после follow-up). То же правило в SQL arena_has_accepted_invite.
 */
export function isAcceptedInvite(i: Pick<CoffeeInviteRow, "status" | "responded_at">): boolean {
  return !!i.responded_at && i.status !== "declined";
}

/** Состояние карточки ARENA по приглашениям на кофе между мной и человеком (в любую сторону). */
export function inviteStateFor(
  invites: CoffeeInviteRow[],
  me: string | undefined,
  other: string,
): { state: InviteState; invite: CoffeeInviteRow | null } {
  if (!me) return { state: "none", invite: null };
  const pair = invites
    .filter(
      (i) =>
        (i.sender_user_id === me && i.recipient_user_id === other) ||
        (i.sender_user_id === other && i.recipient_user_id === me),
    )
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const accepted = pair.find(isAcceptedInvite);
  if (accepted) return { state: "accepted", invite: accepted };
  const last = pair[0];
  if (!last) return { state: "none", invite: null };
  if (last.responded_at) return { state: "declined", invite: last };
  return { state: last.sender_user_id === me ? "sent" : "incoming", invite: last };
}

/** Контакты в карточке ARENA показываем только после принятого приглашения. */
export function showArenaContacts(state: InviteState): boolean {
  return state === "accepted";
}

export type ArenaDayFilter = "all" | 24 | 25;

/** Чип дней на карточке: [24] → «24 окт», [24,25] → «24–25 окт», пусто → null. */
export function formatArenaDays(days: number[] | null | undefined): string | null {
  const list = [...new Set((days ?? []).filter((d) => (ARENA_DAYS as readonly number[]).includes(d)))].sort();
  if (list.length === 0) return null;
  if (list.length === 1) return `${list[0]} окт`;
  return `${list[0]}–${list[list.length - 1]} окт`;
}

/** Подходит ли человек под фильтр дня. */
export function matchesDayFilter(days: number[] | null | undefined, filter: ArenaDayFilter): boolean {
  if (filter === "all") return true;
  return (days ?? []).includes(filter);
}

/** Фильтр по умолчанию: во время фестиваля — сегодняшний день, иначе «Все». */
export function defaultDayFilter(now: Date): ArenaDayFilter {
  return festivalDayToday(now) ?? "all";
}

/** «Сегодня здесь» — только во время фестиваля и если человек сегодня на площадке. */
export function isHereToday(days: number[] | null | undefined, now: Date): boolean {
  return presenceFor(days, now) === "today";
}

/** Дни из выбора на экране «Ты на ARENA?». */
export function arenaDaysValue(choice: "24" | "25" | "both"): number[] {
  return choice === "both" ? [24, 25] : [Number(choice)];
}

/** Поиск в каталоге ARENA: имя, «что ищешь», профессия, «о себе», интересы, сферы. */
export function arenaSearchMatch(
  p: {
    full_name: string;
    looking_for?: string | null;
    profession?: string | null;
    about?: string | null;
    interests?: string[] | null;
    industries?: string[] | null;
  },
  q: string,
): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  const hay = [p.full_name, p.looking_for, p.profession, p.about, ...(p.interests ?? []), ...(p.industries ?? [])];
  return hay.some((v) => (v ?? "").toLowerCase().includes(t));
}

/**
 * Профиль прошёл мастер ARENA и виден в списке (как SQL arena_profile_complete):
 * интересов ≥ MIN_INTERESTS, контакт (Telegram или непустая ссылка) и хотя бы одно описание:
 * профессия, «о себе» или блок эксперта.
 */
export function isArenaProfileComplete(
  p:
    | {
        interests?: string[] | null;
        profession?: string | null;
        about?: string | null;
        expert_category?: string | null;
        expert_categories?: string[] | null;
        expert_offer?: string | null;
        telegram?: string | null;
        social_links?: string[] | null;
      }
    | null
    | undefined,
): boolean {
  if (!p) return false;
  const has = (s: string | null | undefined) => !!(s ?? "").trim();
  const described =
    has(p.profession) || has(p.about) || has(p.expert_category) || has(p.expert_offer) ||
    (p.expert_categories ?? []).length > 0;
  return (
    (p.interests ?? []).length >= MIN_INTERESTS &&
    described &&
    (has(p.telegram) || (p.social_links ?? []).some((s) => has(s)))
  );
}

/** Предохранитель списка ARENA (как SQL arena_people): себя, заполненных и знакомых видно всегда. */
export function isVisibleInArenaList(args: {
  isSelf: boolean;
  profileComplete: boolean;
  hasCompletedMeeting: boolean;
  hasAcceptedInvite: boolean;
}): boolean {
  return args.isSelf || args.profileComplete || args.hasCompletedMeeting || args.hasAcceptedInvite;
}

/** Напоминание «Напиши, кого ищешь»: участник прошёл мастер, а looking_for пуст. */
export function shouldShowSeekingReminder(
  p: Parameters<typeof isArenaProfileComplete>[0] & { looking_for?: string | null },
): boolean {
  return isArenaProfileComplete(p) && !(p?.looking_for ?? "").trim();
}

export type PartnerCircleState = "hidden" | "not-member" | "member";

/** Состояние карточки круга-партнёра ARENA. */
export function partnerCircleState(args: {
  circleId: string;
  circle: { status: string; is_deleted?: boolean | null } | null | undefined;
  isHost: boolean;
  isMember: boolean;
}): PartnerCircleState {
  const { circleId, circle, isHost, isMember } = args;
  if (!circleId || !circle || circle.is_deleted || circle.status !== "published") return "hidden";
  if (isHost) return "hidden";
  return isMember ? "member" : "not-member";
}

/** Показывать ли в разделе ARENA напоминание добавить фото: только участнику без фото. */
export function shouldShowPhotoReminder(opts: {
  isParticipant: boolean;
  photoUrl: string | null | undefined;
}): boolean {
  return opts.isParticipant && !(opts.photoUrl ?? "").trim();
}

/** Склонение «знакомство». */
export function pluralAcquaintances(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  const w = m10 === 1 && m100 !== 11 ? "знакомство" : m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20) ? "знакомства" : "знакомств";
  return `${n} ${w}`;
}

/** Строка «Ещё N до «ступени»» или null, если все ступени пройдены. */
export function nextMilestoneLine(
  n: number,
  labels: Record<number, string>,
  milestones: readonly number[] = ARENA_MILESTONES,
): string | null {
  const next = milestones.find((m) => n < m);
  if (next === undefined) return null;
  return `Ещё ${pluralAcquaintances(next - n)} до «${labels[next]}»`;
}

/** Что показывать над списком: ничего / экран «Задание выполнено» / карточку «Что дальше». */
export function arenaTaskView(completed: number, stickerAt: string | null | undefined): "none" | "claim" | "next" {
  if (completed <= 0) return "none";
  return stickerAt ? "next" : "claim";
}

/** Уже знакомые (подтверждённая встреча) — в конец, остальной порядок сохраняется. */
export function sortAcquaintedLast<T>(items: T[], isAcquainted: (x: T) => boolean): T[] {
  return [...items.filter((x) => !isAcquainted(x)), ...items.filter(isAcquainted)];
}

/** Режим верхнего баннера: «task» — задание с шагами; «continue» — после знакомства и стикера. */
export function arenaBannerMode(completed: number, stickerAt: string | null | undefined): "task" | "continue" {
  return completed > 0 && stickerAt ? "continue" : "task";
}
